import { useQuery } from '@tanstack/react-query';
import { t } from 'i18next';
import { BarChart3, Settings2 } from 'lucide-react';
import { ReactNode } from 'react';

import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { TableTitle } from '@/components/ui/table-title';
import { adminApi } from '@/features/internal-admin/lib/admin-api';
import { formatUtils, useTimeAgo } from '@/lib/utils';
import { AdminHealthOverview } from '@activepieces/shared';

import { CollapsibleStat } from './collapsible-stat';
import {
  RESOURCE_ALERT_PERCENT,
  HEALTH_POLL_INTERVAL_MS,
  SECONDS_TO_MS,
} from './constants';
import {
  hasOrphanRuns,
  hasStuckActiveJob,
  isBacklogTooOld,
  isWorkerless,
} from './health-alerts';
import { busiestCpuPercentage, MachineLines } from './machines-panel';
import { OrphanRunLines } from './orphan-runs-panel';
import { PanelTitle } from './panel-title';
import { longestRunningQueue, oldestWaitingQueue } from './queue-ages';
import { queueLabel } from './queue-labels';
import { RuntimePanel } from './runtime-panel';
import { ServicesPanel } from './services-panel';
import { StatValue } from './stat-value';
import { SystemJobsPanel } from './system-jobs-panel';

const Stat = ({
  label,
  value,
  caption,
  isAlert = false,
}: {
  label: string;
  value: string;
  caption?: ReactNode;
  isAlert?: boolean;
}) => (
  <Card
    className={
      isAlert
        ? 'p-4 flex flex-col gap-1.5 border-destructive/30'
        : 'p-4 flex flex-col gap-1.5'
    }
  >
    <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
      {label}
    </span>
    <StatValue value={value} isAlert={isAlert} />
    {caption && (
      <span className="text-xs text-muted-foreground">{caption}</span>
    )}
  </Card>
);

const DataFreshnessPanel = ({ health }: { health: AdminHealthOverview }) => {
  const { indicatorsFreshness } = health;
  const lastComputedDate = indicatorsFreshness.lastComputedAt
    ? new Date(indicatorsFreshness.lastComputedAt)
    : null;
  const lastComputedAgo = useTimeAgo(lastComputedDate ?? new Date(0));
  const businessPlanDate = health.businessPlanSyncedAt
    ? new Date(health.businessPlanSyncedAt)
    : null;
  const businessPlanAgo = useTimeAgo(businessPlanDate ?? new Date(0));

  return (
    <Card className="flex flex-col">
      <div className="px-4 py-3 border-b">
        <PanelTitle icon={BarChart3} title={t('Indicators pipeline')} />
      </div>
      <div className="px-4 py-3.5 flex flex-col gap-2 text-sm">
        <p className="text-muted-foreground">
          {lastComputedDate
            ? t(
                'Computed {timeAgo} over {accounts} accounts · {months} months of history.',
                {
                  timeAgo: lastComputedAgo,
                  accounts: formatUtils.formatNumber(
                    indicatorsFreshness.tenantsCovered,
                  ),
                  months: indicatorsFreshness.periodsAvailable,
                },
              )
            : t('The nightly indicators job has never run.')}
        </p>
        {indicatorsFreshness.pushFailures > 0 && (
          <p className="text-destructive">
            {t('Accounts that failed to sync to HubSpot: {failures}', {
              failures: formatUtils.formatNumber(
                indicatorsFreshness.pushFailures,
              ),
            })}
          </p>
        )}
        <p className="text-muted-foreground">
          {businessPlanDate
            ? t('Corp business-plan sync: {timeAgo}.', {
                timeAgo: businessPlanAgo,
              })
            : t('Corp business-plan sync has never run.')}
        </p>
        {/* The timestamp is a MAX over tenants that succeeded: per-tenant
            failures are skipped and only logged, so this stays fresh even when
            most tenants are erroring. */}
        <p className="text-xs text-muted-foreground">
          {t(
            'Computed from the tenants that succeeded — a per-tenant failure is only logged, it does not hold this date back.',
          )}
        </p>
      </div>
    </Card>
  );
};

const ConfigLine = ({ label, value }: { label: string; value: string }) => (
  <div className="flex items-baseline justify-between gap-3">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-medium tabular-nums">{value}</span>
  </div>
);

// The values that actually govern the runtime — prod runs a 60 second flow
// timeout, not the 600 second default, and that surprises everyone.
const RuntimeConfigPanel = ({
  config,
}: {
  config: AdminHealthOverview['runtimeConfig'];
}) => (
  <Card className="flex flex-col">
    <div className="px-4 py-3 border-b">
      <PanelTitle icon={Settings2} title={t('Runtime configuration')} />
    </div>
    <div className="px-4 py-3.5 flex flex-col gap-3 text-sm">
      <div className="grid grid-cols-2 gap-x-6 gap-y-2">
        <ConfigLine label={t('Environment')} value={config.environment} />
        <ConfigLine label={t('Edition')} value={config.edition} />
        <ConfigLine label={t('Execution mode')} value={config.executionMode} />
        <ConfigLine
          label={t('Runs on Lambda')}
          value={config.executeWithLambda ? t('yes') : t('no')}
        />
      </div>

      <div className="grid grid-cols-2 gap-x-6 gap-y-2 pt-3 border-t">
        <ConfigLine
          label={t('Flow worker concurrency')}
          value={String(config.flowWorkerConcurrency)}
        />
        <ConfigLine
          label={t('Scheduled worker concurrency')}
          value={String(config.scheduledWorkerConcurrency)}
        />
        <ConfigLine
          label={t('Fair queue')}
          value={config.fairQueueEnabled ? t('enabled') : t('disabled')}
        />
        <ConfigLine
          label={t('Flow rate limit')}
          value={t('{executions} runs / {seconds}s', {
            executions: config.flowRateLimitMaxExecutions,
            seconds: config.flowRateLimitWindowSeconds,
          })}
        />
      </div>

      <div className="grid grid-cols-2 gap-x-6 gap-y-2 pt-3 border-t">
        <ConfigLine
          label={t('Flow timeout')}
          value={t('{seconds}s', { seconds: config.flowTimeoutSeconds })}
        />
        <ConfigLine
          label={t('Trigger timeout')}
          value={t('{seconds}s', { seconds: config.triggerTimeoutSeconds })}
        />
        <ConfigLine
          label={t('Webhook timeout')}
          value={t('{seconds}s', { seconds: config.webhookTimeoutSeconds })}
        />
        <ConfigLine
          label={t('Paused flow timeout')}
          value={t('{days}d', { days: config.pausedFlowTimeoutDays })}
        />
        <ConfigLine
          label={t('Execution data retention')}
          value={t('{days}d', { days: config.executionRetentionDays })}
        />
        <ConfigLine
          label={t('Max file and run output')}
          value={t('{size} MB', { size: config.maxFileSizeMb })}
        />
      </div>

      <div className="grid grid-cols-2 gap-x-6 gap-y-2 pt-3 border-t">
        <ConfigLine
          label={t('Job attempts')}
          value={t('{attempts} · retry after {delay}', {
            attempts: config.jobMaxAttempts,
            delay: formatUtils.formatDuration(
              config.jobRetryBaseDelaySeconds * SECONDS_TO_MS,
              true,
            ),
          })}
        />
        <ConfigLine
          label={t('Trigger auto-disable')}
          value={t('after {failures} failures', {
            failures: formatUtils.formatNumber(config.triggerFailuresThreshold),
          })}
        />
      </div>
    </div>
  </Card>
);

const AdminHealthPage = () => {
  const {
    data: health,
    isLoading,
    isError,
  } = useQuery<AdminHealthOverview, Error>({
    queryKey: ['admin-dashboard-health'],
    queryFn: () => adminApi.getDashboardHealth(),
    refetchInterval: HEALTH_POLL_INTERVAL_MS,
  });

  const oldestWaiting = health ? oldestWaitingQueue(health.queues) : null;
  const longestRunning = health ? longestRunningQueue(health.queues) : null;
  const busiestCpu = health ? busiestCpuPercentage(health.machines) : null;

  return (
    <div className="w-full flex flex-col gap-4">
      <TableTitle
        description={
          <>
            {t(
              'Live state of Khub: dependencies, queues, workers and scheduled jobs.',
            )}
            {health && (
              <span className="ml-1.5 font-mono text-xs">
                {t('v{version}', { version: health.version })}
              </span>
            )}
          </>
        }
      >
        {t('Health')}
      </TableTitle>

      {isError && (
        <Card className="p-4">
          <p className="text-sm text-destructive">
            {t('Failed to load the platform health overview')}
          </p>
        </Card>
      )}

      {isLoading && !health && <Skeleton className="h-96 w-full" />}

      {health && (
        <>
          {/* The per-machine detail hangs off the tile that summarises it,
              like the dependencies and the scheduled jobs above. */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 items-start">
            <CollapsibleStat
              label={t('Workers online')}
              value={formatUtils.formatNumber(health.workersOnline)}
              isAlert={isWorkerless(health)}
              caption={
                busiestCpu !== null && (
                  <span
                    className={
                      busiestCpu >= RESOURCE_ALERT_PERCENT
                        ? 'text-xs font-medium text-destructive'
                        : 'text-xs text-muted-foreground'
                    }
                  >
                    {t('busiest CPU {percent}%', {
                      percent: Math.round(busiestCpu),
                    })}
                  </span>
                )
              }
            >
              <MachineLines machines={health.machines} />
            </CollapsibleStat>

            <Stat
              label={t('Oldest waiting job')}
              value={
                oldestWaiting
                  ? formatUtils.formatDuration(
                      oldestWaiting.oldestJobAgeSeconds * SECONDS_TO_MS,
                      true,
                    )
                  : '—'
              }
              caption={
                oldestWaiting
                  ? queueLabel(oldestWaiting.queueName)
                  : t('no backlog')
              }
              isAlert={isBacklogTooOld(health)}
            />
            <Stat
              label={t('Longest running job')}
              value={
                longestRunning
                  ? formatUtils.formatDuration(
                      longestRunning.oldestActiveJobAgeSeconds * SECONDS_TO_MS,
                      true,
                    )
                  : '—'
              }
              caption={
                longestRunning
                  ? queueLabel(longestRunning.queueName)
                  : t('nothing running')
              }
              isAlert={hasStuckActiveJob(health)}
            />
            {health.orphanRuns === null ? (
              <Stat
                label={t('Orphan runs')}
                value="—"
                caption={t('not counted yet')}
              />
            ) : (
              <CollapsibleStat
                label={t('Orphan runs')}
                value={formatUtils.formatNumber(health.orphanRuns.total)}
                isAlert={hasOrphanRuns(health)}
                caption={
                  <span className="text-xs text-muted-foreground">
                    {t('no final status for {hours}h+', {
                      hours: health.orphanRuns.idleHours,
                    })}
                  </span>
                }
              >
                <OrphanRunLines orphanRuns={health.orphanRuns} />
              </CollapsibleStat>
            )}
          </div>

          <ServicesPanel health={health} />

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
            <RuntimePanel health={health} />
            <DataFreshnessPanel health={health} />
          </div>

          <SystemJobsPanel systemJobs={health.systemJobs} />

          {/* Last on purpose: reference values that only move with a deploy,
              read once something above has turned red. */}
          <RuntimeConfigPanel config={health.runtimeConfig} />
        </>
      )}
    </div>
  );
};

export default AdminHealthPage;
